import { clsx } from 'clsx';
import { useId } from 'react';

/**
 * Minus and plus around a big number, for drill makes and the target score. 56 px buttons.
 * The number is a spinbutton, so arrow keys, Page Up and Page Down work on it too.
 *
 *   <Stepper label="Makes" value={makes} min={0} max={25} onChange={setMakes} />
 */
export interface StepperProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Shown under the number, "of 25". */
  suffix?: string;
  className?: string;
}

export function clampStep(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

export function Stepper({ label, value, onChange, min = 0, max = 99, step = 1, suffix, className }: StepperProps) {
  const id = useId();
  const set = (v: number) => {
    const next = clampStep(v, min, max);
    if (next !== value) onChange(next);
  };
  const btn = 'tts-btn size-14 min-h-0 shrink-0 p-0 text-3xl not-italic disabled:opacity-40';
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <span id={`${id}-l`} className="text-sm font-bold uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className={btn}
          aria-label={`${label} minus ${step}`}
          disabled={value <= min}
          onClick={() => set(value - step)}
        >
          <span aria-hidden="true">−</span>
        </button>
        <div
          role="spinbutton"
          tabIndex={0}
          aria-labelledby={`${id}-l`}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          onKeyDown={(e) => {
            const map: Record<string, number> = {
              ArrowUp: value + step,
              ArrowRight: value + step,
              ArrowDown: value - step,
              ArrowLeft: value - step,
              PageUp: value + step * 5,
              PageDown: value - step * 5,
              Home: min,
              End: max,
            };
            const next = map[e.key];
            if (next === undefined) return;
            e.preventDefault();
            set(next);
          }}
          className="flex min-h-14 min-w-20 flex-1 flex-col items-center justify-center rounded-tts border-(length:--tts-border) border-ink bg-white"
        >
          <span className="tts-display text-4xl tabular-nums">{value}</span>
          {suffix && <span className="text-xs font-semibold">{suffix}</span>}
        </div>
        <button
          type="button"
          className={clsx(btn, 'tts-btn-primary')}
          aria-label={`${label} plus ${step}`}
          disabled={value >= max}
          onClick={() => set(value + step)}
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
    </div>
  );
}
